/* What can be bought, and what it costs.
 *
 * One file, imported by the credits screen, the pricing page, the landing
 * teaser and How it works, so a price appears in the product in exactly one
 * shape and changes in exactly one place.
 *
 * **Every price is integer pence.** No floats: 0.1 + 0.2 is not 0.3 and a
 * money screen is the last place to find that out. The currency travels with
 * the number rather than being assumed by the formatter, so a second currency
 * later is a data change and not a hunt through screens.
 *
 * The operator set these on 4 September 2026: 5 pages 3.99, 20 pages 12.99,
 * 60 pages 29.99, and 6.99 a month for 15 pages. They are VAT inclusive, and
 * the two screens that sell say so.
 *
 * **There is still no payment rail.** The Buy control opens a sheet that says
 * checkout is coming and what the pack would do. Nothing here takes money and
 * nothing here should look as though it does. A price is not a checkout.
 *
 * The rules the screens must keep, so they are written beside the data:
 * nothing is preselected, nothing is pushed, there is no countdown and no
 * "most popular" badge. Practice is always free. A page we cannot mark is
 * always free. The cost sentence comes before any spend and the balance is
 * visible before a send.
 */

import type { Entitlement, PlanState } from "./types";

/** The only currency the product prices in today. Written once; every item
 *  carries it as a field so nothing downstream has to assume it. */
export const CURRENCY = "GBP" as const;
export type Currency = typeof CURRENCY;

/** Prices include VAT, and the two screens that sell a pack say this line. */
export const VAT_LINE = "Prices include VAT.";

export interface Pack {
  /** Pages this pack marks. One credit marks one page. */
  pages: number;
  /** Stable across a price change, so a screen can key on it. */
  id: string;
  /** The price, in whole pence. Never a float, never a formatted string. */
  pence: number;
  currency: Currency;
  /** What this pack is for, in one clause. Never a claim about results. */
  note: string;
}

/** Packs of 5, 20 and 60 pages. In size order and in no other order: a
 *  recommended one would be a push, and there is no recommended one. */
export const PACKS: readonly Pack[] = [
  {
    id: "pack-5", pages: 5, pence: 399, currency: CURRENCY,
    note: "A term's worth of the odd tricky page.",
  },
  {
    id: "pack-20", pages: 20, pence: 1299, currency: CURRENCY,
    note: "Roughly a page a week for two terms.",
  },
  {
    id: "pack-60", pages: 60, pence: 2999, currency: CURRENCY,
    note: "A whole paper at a time, or a second child.",
  },
];

export interface Plan {
  id: string;
  /** Pages included each month. */
  pages: number;
  pence: number;
  currency: Currency;
  note: string;
}

/** What the product says about the monthly plan, everywhere it says anything.
 *
 *  **One sentence, in one place, because three screens disagreed.** The
 *  pricing page said the plan could be stopped from your account, the landing
 *  said the same, and the credits screen said the terms were not set and there
 *  was no way to start one. Two of those were claims about a recurring payment
 *  the product's own third screen said nobody had settled, and there is no
 *  payment rail to settle them on. So renewal, cancellation and what happens
 *  to unused credits are stated as what they are: not written yet.
 *
 *  Named here so `credits`, `landing` and `pricing` read the same string and
 *  cannot drift apart again. */
export const MONTHLY_TERMS =
  "The monthly plan is not on sale yet. When it is, its terms will be here first.";

/** The monthly plan, beside the packs and not above them. */
export const MONTHLY: Plan = {
  id: "monthly",
  pages: 15,
  pence: 699,
  currency: CURRENCY,
  note: MONTHLY_TERMS,
};

/** What one credit does, said the way it is said everywhere else. */
export const CREDIT_SENTENCE =
  "One credit marks one page. A page we cannot mark is free.";

const gbp = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Pence as a parent reads them: 399 is "3.99" with a pound sign. */
export function formatPrice(pence: number): string {
  return gbp.format(pence / 100);
}

/** Anything with a price and a number of pages: a pack, the plan, or the two
 *  numbers on their own, which is what `PriceTag` holds. */
export interface Priced {
  pence: number;
  pages: number;
}

/** The price of a pack or the plan, formatted. A function rather than a field
 *  so a screen never formats money for itself. */
export function priceOf(item: Priced): string {
  return formatPrice(item.pence);
}

/** What one page works out at, in whole pence, rounded to the nearest penny.
 *  Integer arithmetic on the way in, one rounding on the way out. */
export function pencePerPage(item: Priced): number {
  return Math.round(item.pence / item.pages);
}

/** The small line under a price. Under a pound it reads "80p a page", because
 *  that is how the number is said out loud. At a pound and over it wears the
 *  pound sign, since "104p a page" is not English. */
export function perPageOf(item: Priced): string {
  const each = pencePerPage(item);
  return each < 100
    ? `${each}p a page`
    : `${formatPrice(each)} a page`;
}

/** The pack with the lowest per-page rate.
 *
 *  Computed rather than typed for the obvious reason: the packs run 80p, 65p
 *  and 50p, and a price edited in this file would otherwise leave a stale
 *  number on the front page. Integer pence in, one comparison, no floats.
 *
 *  A pack rather than every product. `cheapestPerPage` used to answer across
 *  the packs AND the monthly plan, which made it 47p, and 47p is a rate on a
 *  product that is not on sale: its terms are not settled and no screen offers
 *  a way to start one. Quoting it was the same promise as promising the terms.
 *  The landing computed its own version of this to get round that and nothing
 *  called the exported one, so the rule is written here once and the landing
 *  imports it. */
export function cheapestPack(): Pack {
  let best = PACKS[0]!;
  for (const pack of PACKS) {
    if (pencePerPage(pack) < pencePerPage(best)) best = pack;
  }
  return best;
}

/* ------------------------------------------ Agent V8-ahead, wave 3, additive.
 *
 * The plan a parent is actually on, as opposed to the plan this file sells.
 *
 * **Everything below reads the service and states nothing of its own.** The
 * period, whether another month is coming, and how many of this month's pages
 * are left are all the service's answers, carried across and put into
 * sentences. Nothing here computes a date, a renewal or a balance, for the
 * reason `meter.ts` computes none either: two places holding an opinion about
 * money is two places that can disagree, and the day they do it is a family's
 * money the disagreement is about.
 *
 * The plan is null for every parent today, because there is no payment rail
 * and nothing has ever written a period. These functions exist so that the
 * screens are ready for the day one does, and so that the day it does they
 * read one file rather than three.
 */

/** The plan on an entitlement, or null. Null and absent are one answer: there
 *  is no plan to say anything about. */
export function planOf(ent: Entitlement | null | undefined): PlanState | null {
  return ent?.plan ?? null;
}

/** A date the service sent, as a parent reads it: "1 November 2026".
 *
 *  **Formatted in UTC and not in the phone's zone**, which is the one thing
 *  worth getting right here. The service names the day a month ends, at
 *  midnight UTC. Rendered in a zone behind UTC that midnight is the evening
 *  before, so a parent in Lisbon or New York would be told their plan ends a
 *  day early. The date is the service's fact and the phone does not get a
 *  vote on it.
 *
 *  An unparseable date is an empty string, and the callers below drop the
 *  sentence rather than print "Invalid Date" at somebody. */
export function planDate(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  }).format(when);
}

/** Where the plan stands, in one sentence.
 *
 *  Two states and no third. It renews, and the date is when the next pages
 *  arrive; or it has been cancelled, and the date is when the month a parent
 *  paid for runs out. There is no "your plan has ended" because the service
 *  stops sending a plan on the day it ends, and a screen still talking about
 *  a finished subscription is a screen about nothing.
 *
 *  Empty where the date could not be read, so a caller renders nothing rather
 *  than half a sentence. */
export function planLine(plan: PlanState): string {
  const when = planDate(plan.period_end);
  if (!when) return "";
  return plan.renews
    ? `Your plan renews on ${when}.`
    : `You have cancelled your plan. It runs until ${when}.`;
}

/** How much of this month's pages are left, in words. Never a claim about the
 *  balance: a parent may hold credits from a pack as well, and this counts
 *  only the plan's month. */
export function planPagesLeft(plan: PlanState): string {
  const left = plan.pages_left_this_period;
  return left === 1
    ? "1 page left this month."
    : `${left} pages left this month.`;
}
